import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { Parallax } from 'react-parallax';

//image
import heroImage from '../images/txoko images/lugar/txoko55.webp';

import section1Image from '../images/txoko images/platos/txoko23.webp';
import section2Image from '../images/txoko images/platos/txoko51.webp';
import section3Image from '../images/txoko images/platos/txoko50.webp';

import galleryImage1 from '../images/txoko images/lugar/txoko56.webp';
import galleryImage2 from '../images/txoko images/platos/txoko47.webp';
import galleryImage3 from '../images/txoko images/platos/txoko05.webp';
import galleryImage4 from '../images/txoko images/platos/txoko54.webp';

function GastronomyPage() {
  const [hasViewedSection1, setHasViewedSection1] = useState(false);
  const [hasViewedSection2, setHasViewedSection2] = useState(false);
  const [hasViewedSection3, setHasViewedSection3] = useState(false);

  const { ref: refSection1, inView: inViewSection1 } = useInView({ threshold: 0.2, triggerOnce: true });
  const { ref: refSection2, inView: inViewSection2 } = useInView({ threshold: 0.2, triggerOnce: true });
  const { ref: refSection3, inView: inViewSection3 } = useInView({ threshold: 0.2, triggerOnce: true });

  useEffect(() => {
    if (inViewSection1 && !hasViewedSection1) {
      setHasViewedSection1(true);
    }
  }, [inViewSection1, hasViewedSection1]);

  useEffect(() => {
    if (inViewSection2 && !hasViewedSection2) {
      setHasViewedSection2(true);
    }
  }, [inViewSection2, hasViewedSection2]);

  useEffect(() => {
    if (inViewSection3 && !hasViewedSection3) {
      setHasViewedSection3(true);
    }
  }, [inViewSection3, hasViewedSection3]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const gastronomyData = [
    {
      title: "Entrantes Frescos del Mar",
      description:
        "Comienza tu experiencia culinaria con una selección de entrantes que capturan la esencia del Mediterráneo. Desde carpaccio de lubina con un toque de cítricos hasta tartar de atún rojo con aguacate y sésamo, cada plato está diseñado para despertar tus sentidos.",
      image: section1Image,
    },
    {
      title: "Platos Principales que Celebran la Tradición",
      description:
        "Nuestros platos principales son un homenaje a la rica tradición culinaria de la región. Disfruta de una paella de mariscos cocinada a la perfección, un suculento rodaballo a la plancha o un tierno cordero lechal asado lentamente. Cada ingrediente es seleccionado por su calidad y frescura.",
      image: section2Image,
    },
    {
      title: "Pescados Frescos con Sabor a Mar",
      description:
        "El Mediterráneo nos regala una variedad extraordinaria de pescados que son la base de platos emblemáticos de la cocina costera. Nuestras recetas respetan la frescura y calidad de estos tesoros marinos, preparándolos con técnicas que realzan su sabor natural.",
      image: section3Image,
    },
  ];

  // Animación de aparición gradual con desplazamiento desde abajo
  const fadeInUpVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div className="font-montserrat bg-gray-50 text-gray-800">

      {/* Hero Section */}
      <div className="relative w-full">
        <Parallax bgImage={heroImage} strength={100} bgImageStyle={{ objectFit: 'cover' }}>
          {/* Overlays */}
          <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent z-0 opacity-60"></div>
          <div className="absolute inset-0 bg-black opacity-30 z-0"></div>

          <section className="flex flex-col items-center justify-center relative h-[15vh] sm:h-[20vh] md:h-[20vh] lg:h-[30vh] xl:h-[40vh] mx-auto px-4 sm:px-6 md:px-8 lg:px-16 xl:px-24 overflow-hidden">
            <div
              className="z-10 text-center text-white md:-mt-36"
              initial={{ opacity: 0, y: 100 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: "easeOut" }}
            >
              <h1 className="font-barlow uppercase font-[600] text-4xl sm:text-5xl md:text-6xl lg:text-8xl mb-4">
                Gastronomía
              </h1>
            </div>
          </section>
        </Parallax>

        {/* Diagonal Divider */}
        <div className="absolute bottom-0 left-0 w-full overflow-hidden" style={{ marginBottom: "-1px" }}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1440 110"
            className="w-full"
          >
            <path
              fill="#f9fafb"
              fillOpacity="1"
              d="M0,30 L1440,0 L1440,110 L0,110 Z"
            />
          </svg>
        </div>
      </div>

      {/* Main Content */}
      <main className="bg-gray-50">
        {/* Intro Section */}
        <motion.section
          className="text-center mb-10 md:mb-16 px-6 md:px-12 lg:px-24 max-w-4xl mx-auto"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUpVariants}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <h2 className="font-barlow text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            Descubre la Auténtica Cocina Mediterránea
          </h2>
          <p className="text-base md:text-lg lg:text-xl text-gray-600 mb-32">
            En Txoko, nos enorgullecemos de ofrecer una experiencia culinaria única, donde cada plato es una obra de arte y cada bocado una celebración de los sabores de Menorca. Utilizamos solo los ingredientes más frescos y de la más alta calidad, provenientes de productores locales y del mar que nos rodea.
          </p>
        </motion.section>

        {/* Gastronomy Sections with Images to the Side */}
        {gastronomyData.map((section, index) => (
          <motion.section
            key={index}
            ref={
              index === 0
                ? refSection1
                : index === 1
                  ? refSection2
                  : refSection3
            }
            className="mb-10 md:mb-16"
            initial="hidden"
            animate={
              (index === 0 && inViewSection1) ||
                (index === 1 && inViewSection2) ||
                (index === 2 && inViewSection3)
                ? "visible"
                : "hidden"
            }
            variants={fadeInUpVariants}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="container mx-auto px-6 md:px-12 lg:px-24 flex flex-col md:flex-row items-center">
              {/* Image Container */}
              <div className={`w-full md:w-1/2 ${index % 2 !== 0 ? 'md:order-2' : ''}`}>
                <img
                  src={section.image}
                  alt={section.title}
                  className="w-full h-auto rounded-lg shadow-lg object-cover"
                />
              </div>

              {/* Text Container */}
              <div className={`w-full md:w-1/2 text-center md:text-left ${index % 2 !== 0 ? 'md:order-1' : ''} mt-6 md:mt-0`}>
                <div className={index % 2 !== 0 ? 'md:mr-10' : 'md:ml-10'}>
                  <h3 className="font-barlow text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-gray-800">
                    {section.title}
                  </h3>
                  <p className="text-ubuntu md:text-lg lg:text-xl text-gray-600">
                    {section.description}
                  </p>
                </div>
              </div>
            </div>
          </motion.section>
        ))}
      </main>

      {/* Galeria de imagenes responsive */}
      <section className="container mx-auto px-6 md:px-12 lg:px-24 mb-12 md:mb-20 mt-24">
        <h2 className="font-barlow text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 text-center mb-4 md:mb-12">
          Nuestra Experiencia Gastronómica
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-6 md:gap-8">
          <motion.div
            className="relative overflow-hidden rounded-lg shadow-lg"
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            <img
              src={galleryImage1}
              alt="Gastronomy Gallery"
              className="w-full h-full object-cover"
            />
          </motion.div>
          <motion.div
              className="relative overflow-hidden rounded-lg shadow-lg"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
            >
              <img
                src={galleryImage2}
                alt="Gastronomy Gallery"
                className="w-full h-full object-cover"
              />
            </motion.div>
            <motion.div
              className="relative overflow-hidden rounded-lg shadow-lg"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
            >
              <img
                src={galleryImage3}
                alt="Gastronomy Gallery"
                className="w-full h-full object-cover"
              />
            </motion.div>
            <motion.div
              className="relative overflow-hidden rounded-lg shadow-lg"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
            >
              <img
                src={galleryImage4}
                alt="Gastronomy Gallery"
                className="w-full h-full object-cover"
              />
            </motion.div>
        </div>
      </section>


      {/* Reserva Mesa Call to action */}
      <Parallax
        bgImage={heroImage}
        strength={300}
        bgImageStyle={{ objectFit: 'cover' }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent z-0 opacity-75"></div>
        <div className="absolute inset-0 bg-black opacity-40 z-0"></div>
        <section className="py-24 sm:py-32 md:py-44 relative z-10">
          <div className="container mx-auto text-center px-4 py-12 lg:mb-24">
            <h2 className="text-white font-bebas text-[50px] sm:text-[70px] md:text-[100px] font-extralight -mb-2 leading-tight">
              Reserva tu mesa ahora
            </h2>
            <p className="uppercase font-montserrat text-white text-[16px] sm:text-[18px] md:text-[20px] mt-2 sm:mt-4">
              Ven a vernos y disfruta de la mejor comida de mar en Menorca
            </p>
            <div className="flex justify-center mt-8 sm:mt-12">
              <Link
                className="custom-link uppercase font-montserrat px-5 sm:px-7 py-2 sm:py-[6.5px] text-sm sm:text-lg font-semibold text-white bg-transparent border-[2px] sm:border-[2.5px] border-white shadow-md transform transition-all ease-in-out duration-300 hover:scale-95 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-75"
                to="/contact"
              >
                Reserva ahora!
              </Link>
            </div>
          </div>
        </section>

        {/* Línea diagonal en la parte superior */}
        <div className="absolute top-0 left-0 w-full">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1440 110"
            className="w-full"
          >
            <path
              fill="#f9fafb"
              fillOpacity="1"
              d="M0,60 L1440,0 L1440,0 L0,0 Z"
            />
          </svg>
        </div>
      </Parallax>

      {/* División Linea Diagonal */}
      <div className="relative bg-gray-50">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1440 110"
          className="absolute bottom-0 left-0 w-full"
        >
          <path
            fill="#f9fafb"
            fillOpacity="1"
            d="M0,60 L1440,0 L1440,1100 L0,1100 Z"
          />
        </svg>
      </div>
    </div>
  );
}

export default GastronomyPage;